import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { NotificationService } from '../../../../../../common/services';
import { AllocationActionPrepareCallToDockModel, AllocationDetailsModel } from '../../models';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AllocationViewCallToDockActionPrepare,
  AllocationViewCallToDockAction,
  AllocationViewCallToDockActionLoadAllocationDetails,
  AllocationViewCallToDockActionAllocateUli,
  AllocationViewCallToDockActionSetInitialState,
} from './allocation-view-call-to-dock-action.actions';
import { AllocationViewCallToDockActionService } from './allocation-view-call-to-dock-action.service';
import { Injectable } from '@angular/core';
import { AllocationViewActionBaseState } from '../base';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface AllocationViewCallToDockActionStateModel {
  loaded: boolean;
  callToDockDetails: AllocationActionPrepareCallToDockModel;
  allocationDetails: AllocationDetailsModel;
}

const initialState: AllocationViewCallToDockActionStateModel = {
  loaded: false,
  callToDockDetails: null,
  allocationDetails: null,
};

@State({
  name: 'allocationViewActionsCallToDock',
  defaults: initialState,
})
@Injectable()
export class AllocationViewCallToDockActionState extends AllocationViewActionBaseState {
  @Selector()
  static getLoaded(state: AllocationViewCallToDockActionStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getCallToDockDetails(state: AllocationViewCallToDockActionStateModel): AllocationActionPrepareCallToDockModel {
    return state.callToDockDetails;
  }

  @Selector()
  static getAllocationDetails(state: AllocationViewCallToDockActionStateModel): AllocationDetailsModel {
    return state.allocationDetails;
  }

  constructor(
    private allocationViewCallToDockActionService: AllocationViewCallToDockActionService,
    notificationService: NotificationService,
    store: Store,
  ) {
    super(notificationService, store);
  }

  @Action(AllocationViewCallToDockActionPrepare)
  callToDockPrepare(
    ctx: StateContext<AllocationViewCallToDockActionStateModel>,
    { payload: { identifier } }: AllocationViewCallToDockActionPrepare,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | AllocationActionPrepareCallToDockModel> {
    return this.allocationViewCallToDockActionService.callToDockPrepare(identifier).pipe(
      tap((callToDockDetails: AllocationActionPrepareCallToDockModel) => {
        ctx.patchState({ callToDockDetails, loaded: true });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewCallToDockAction)
  callToDock(
    ctx: StateContext<AllocationViewCallToDockActionStateModel>,
    { payload: { identifier } }: AllocationViewCallToDockAction,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const { callToDockDetails } = ctx.getState();
    return this.allocationViewCallToDockActionService
      .callToDock({ ...identifier, callToDockTimestamp: callToDockDetails.callToDockTimestamp })
      .pipe(
        tap(() => {
          this.finishAction('api.uli.success.calledToDock');
        }),
        catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
      );
  }

  @Action(AllocationViewCallToDockActionLoadAllocationDetails)
  getAllocationDetails(
    ctx: StateContext<AllocationViewCallToDockActionStateModel>,
    { payload: { identifier } }: AllocationViewCallToDockActionLoadAllocationDetails,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewCallToDockActionService.getAllocationDetails(identifier).pipe(
      map((allocationDetails: AllocationDetailsModel) => {
        ctx.patchState({ allocationDetails, loaded: true });
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewCallToDockActionAllocateUli)
  allocateUli(
    ctx: StateContext<AllocationViewCallToDockActionStateModel>,
    { payload }: AllocationViewCallToDockActionAllocateUli,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewCallToDockActionService.allocateUli(payload).pipe(
      map(() => {
        this.finishAction('api.uli.success.allocate', false);
      }),
      catchError(({ error }: HttpErrorResponse) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewCallToDockActionSetInitialState)
  setInitialState(ctx: StateContext<AllocationViewCallToDockActionStateModel>): void {
    ctx.setState(initialState);
    this.setContainerAfterActionEffectsInitialState();
  }
}
